import React, { forwardRef, useCallback } from 'react';
import clsx from 'classnames';
import { SurveyTheme } from '@sm/webassets/dist/components/SurveyTheme/types';
import { createSurveyStyles } from '@sm/webassets';
import { useSurveyFormatContext } from '../useSurveyFormatContext';
import { SurveyFormatViewProps } from '../types';
import { findFirstFocusable } from '../utils';
import useFormatView from './useFormatView';

const useStyles = createSurveyStyles<'shield', { active: boolean; noShield: boolean; autoPadding: boolean }>(
  (theme: SurveyTheme) => ({
    shield: {
      opacity: ({ active, noShield }) => (!!noShield || active ? 1 : 0.5),
      paddingBottom: ({ autoPadding }) => (autoPadding ? '80px' : 0),
      [`@media (max-width: ${theme.breakpoints?.xs.max})`]: {
        paddingBottom: ({ autoPadding }) => (autoPadding ? '270px' : 0),
      },
    },
  })
);

export function OQAATFormatView(
  { name, noShield = false, autoPadding = true, className, children, ...props }: SurveyFormatViewProps,
  forwardedRef?: React.Ref<HTMLDivElement>
): React.ReactElement {
  const formatContext = useSurveyFormatContext();
  const { setActive, setNextActive } = formatContext;

  const { isActive, ref } = useFormatView(name, formatContext, forwardedRef);

  const { shield } = useStyles({ active: isActive, noShield, autoPadding });

  const handleShieldClick = useCallback(() => {
    if (!isActive && ref?.current) {
      const firstFocusable = findFirstFocusable(ref.current);
      firstFocusable?.focus();
    }
  }, [isActive, ref]);

  const handleFocus = useCallback(
    (e: React.FocusEvent) => {
      const ele = ref?.current as HTMLElement;
      if (!isActive && ele !== e.target && ele.contains(e.target)) {
        setActive(name);
      }
    },
    [name, ref, isActive, setActive]
  );

  let content = children;
  if (typeof children === 'function') {
    content = children({ isActive, setNextActive });
  }
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div onFocus={handleFocus} onClick={handleShieldClick} className={clsx(shield, className)} {...props} ref={ref}>
      {content}
    </div>
  );
}

export default forwardRef<HTMLDivElement, SurveyFormatViewProps>(OQAATFormatView);
